<script setup lang="ts">
import { fromNow } from '@thecompaniesapi/date-helpers'
import { slugify } from '@thecompaniesapi/string-helpers'
import type { PaginationMeta, Prompt, PromptLoadingState } from '@thecompaniesapi/shared/client'

const props = defineProps({
  company: {
    required: true,
    type: Object as PropType<CompanyV2>,
  },
  showContext: {
    default: false,
    type: Boolean,
  },
  showFeature: {
    default: false,
    type: Boolean,
  },
})

const currentPrompt = defineModel<string>('prompt', { default: '' })
const currentLoadingState = defineModel<PromptLoadingState | undefined>('loadingState', { default: undefined })
const currentPromptKey = computed(() => {
  return slugify(currentPrompt.value)
})

const api = useApi()
const { t } = useI18n()
const localePath = useLocalePath()

const prompts = ref<(Prompt<'ask'> & { loading?: boolean })[]>([])
const meta = ref<PaginationMeta>()
const loading = ref(false)
const promptDeleting = ref<number>()

async function fetchPrompts(params?: {
  page?: number
  search?: string
  size?: number
}) {
  const response = await api.prompts.fetch({
    companyId: props.company?.meta?.mysqlId,
    feature: 'ask',
    page: params?.page,
    search: params?.search,
    size: params?.size,
  })

  prompts.value = response.data.prompts as (Prompt<'ask'> & { loading?: boolean })[]
  meta.value = response.data.meta
}

async function submitPrompt() {
  loading.value = true

  try {
    if (!props.company?.domain?.domain || !currentPrompt.value) {
      return
    }

    const createdAt = new Date()

    addOrReplacePrompt({
      companyId: props.company?.meta?.mysqlId,
      context: 'company',
      createdAt,
      feature: 'ask',
      id: -1,
      loading: true,
      prompt: currentPrompt.value,
      promptKey: slugify(currentPrompt.value),
    })

    try {
      const response = await api.companies.ask({
        domain: props.company?.domain?.domain,
        explain: true,
        question: currentPrompt.value,
      })

      addOrReplacePrompt(response.data.prompt as Prompt<'ask'> & { loading?: boolean })

      currentPrompt.value = ''
    }
    catch {
      prompts.value = prompts.value.filter(prompt => prompt.id !== -1)
    }
  }
  catch {}

  loading.value = false
}

async function addOrReplacePrompt(prompt: Prompt<'ask'> & { loading?: boolean }) {
  const index = prompts.value.findIndex(pIndex => pIndex.promptKey === prompt.promptKey)

  prompts.value = index !== -1
    ? [...prompts.value.map((promptIndex, cIndex) => cIndex === index ? prompt : promptIndex)]
    : [prompt, ...prompts.value.slice(0, 9)]
}

async function onDeletePrompt(promptId: number) {
  promptDeleting.value = promptId

  try {
    const response = await api.prompts.delete(promptId)

    prompts.value = prompts.value.filter(prompt => prompt.id !== response.data.id)
  }
  catch (error) {
    console.error(error)
  }

  promptDeleting.value = undefined
}

// Fetch prompts on mount if none preloaded
onMounted(async () => {
  if (!prompts?.value?.length) {
    await fetchPrompts()
  }
})

defineExpose({
  currentLoadingState,
  loading,
  submitPrompt,
})

await callOnce(
  `fetch-prompts-${props.company?.meta?.mysqlId}`,
  fetchPrompts,
  { mode: 'navigation' },
)

// Helper function to determine badge color based on score
function getBadgeColor(score: number): 'emerald' | 'orange' | 'red' | 'yellow' {
  if (score < 25) {
    return 'red'
  }
  if (score < 50) {
    return 'orange'
  }
  if (score < 70) {
    return 'yellow'
  }

  return 'emerald'
}

useEvent(
  'user',
  'prompt:step',
  (data) => {
    if (data.promptKey === currentPromptKey.value) {
      currentLoadingState.value = data.step
    }
  },
)
</script>

<template>
  <div class="flex flex-col gap-4">
    <template v-if="prompts.length">
      <UCard
        v-for="prompt in prompts"
        :key="prompt.promptKey"
        class="gradient-texture-reverse"
        :ui="{
          body: {
            padding: '!p-1',
          },
        }"
      >
        <div class="flex size-full flex-col">
          <UBadge
            class="w-full"
            :icon="getIcon('ask')"
            size="lg"
            :ui="{
              base: prompt.feature === 'ask' && !!prompt?.response?.answer?.explanation ? '!rounded-b-none !rounded-t-xl !border-b-transparent' : '!rounded-xl',
              icon: {
                base: 'self-start mt-1',
              },
            }"
            variant="subtle"
          >
            <AnimateRadiantText
              :active="!!prompt.loading"
              class="!text-base leading-relaxed"
            >
              {{ prompt.prompt }}
            </AnimateRadiantText>
          </UBadge>

          <UBadge
            v-if="prompt.feature === 'ask' && !!prompt?.response?.answer?.explanation"
            class="w-full"
            color="blue"
            :icon="getIcon('results')"
            size="lg"
            :ui="{
              base: '!rounded-b-xl !rounded-t-none',
              icon: {
                base: 'self-start mt-1',
              },
            }"
            variant="subtle"
          >
            <p class="!text-base leading-relaxed">
              {{ prompt.response.answer.explanation }}
            </p>
          </UBadge>
        </div>

        <div
          v-if="prompt.feature === 'ask' && !!prompt?.response?.answer"
          class="flex flex-col gap-2 p-2"
        >
          <div
            v-if="prompt.response.answer.output"
            class="grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <template
              v-for="(value, key) in prompt.response.answer.output"
              :key="key"
            >
              <div class="bg-texture-deep ring-texture overflow-hidden rounded-lg">
                <div class="gradient-texture-reverse flex flex-col gap-1 p-2">
                  <div class="font-mono text-sm">{{ key }}</div>

                  <span class="text-sm">
                    {{ prompt?.data?.fields?.find(field => field.key === key)?.description }}
                  </span>
                </div>

                <div class="p-2 font-mono">
                  <template v-if="Array.isArray(value)">
                    <ul class="list-disc space-y-1 pl-4">
                      <li
                        v-for="(item, index) in value"
                        :key="index"
                        class="text-sm"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </template>

                  <template v-else-if="typeof value === 'boolean'">
                    <span class="text-sm font-medium">
                      {{ value ? t('values.true') : t('values.false') }}
                    </span>
                  </template>

                  <template v-else>
                    <span class="text-sm">{{ value }}</span>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="flex w-full justify-between px-2 py-1">
          <div class="flex items-center gap-2">
            <UBadge
              v-if="showContext && prompt.context"
              class="capitalize"
              color="gray"
              size="xs"
            >
              {{ t(`badges.context.${prompt.context}`) }}
            </UBadge>

            <UBadge
              v-if="showFeature && prompt.feature"
              class="capitalize"
              color="indigo"
              size="xs"
            >
              {{ t(`badges.feature.${prompt.feature}`) }}
            </UBadge>

            <UButton
              v-if="prompt.feature === 'ask' && typeof prompt.response?.answer?.score === 'number'"
              class="pointer-events-none"
              size="2xs"
            >
              <span class="flex items-center gap-1">
                <UBadge
                  :color="getBadgeColor(prompt.response.answer.score)"
                  size="xs"
                  variant="subtle"
                >
                  {{ prompt.response.answer.score }}
                </UBadge>

                {{ t('badges.score') }}
              </span>
            </UButton>

            <ButtonClipboard
              v-if="prompt.feature === 'ask' && prompt?.response?.answer?.output"
              size="xs"
              :string-to-copy="prompt"
            />

            <UTooltip :text="t('buttons.deletePrompt')">
              <UButton
                color="red"
                :icon="getIcon('trash')"
                :loading="promptDeleting === prompt.id!"
                size="2xs"
                variant="subtle"
                @click="onDeletePrompt(prompt.id)"
              />
            </UTooltip>
          </div>

          <UButton
            v-if="prompt.createdAt"
            size="2xs"
            variant="link"
          >
            <span class="text-xs">
              {{ fromNow(prompt.createdAt) }}
            </span>
          </UButton>
        </div>
      </UCard>
    </template>

    <template v-else>
      <div class="flex flex-col gap-4 p-4">
        <BaseQuote
          color="gray"
          :icon="getIcon('ask')"
        >
          <div class="flex flex-col gap-2">
            <p class="text-base text-gray-700 dark:text-gray-300">
              {{ t('prompts.ask') }}
            </p>

            <div class="flex items-center justify-end gap-2">
              <UButton
                :icon="getIcon('docsUrl')"
                :label="t('prompts.openInDocs')"
                size="xs"
                :to="localePath({
                  name: 'api',
                  hash: '#companies-ask',
                })"
                :ui="{
                  icon: {
                    base: 'text-indigo-500 dark:text-indigo-400',
                  },
                }"
              />
            </div>
          </div>
        </BaseQuote>
      </div>
    </template>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "badges": {
      "context": {
        "company": "Company",
        "list": "List",
        "team": "Team"
      },
      "feature": {
        "ask": "Ask",
        "enrich": "Enrich",
        "search": "Search",
        "similar": "Similar"
      },
      "processing": "Processing",
      "score": "Certainty"
    },
    "buttons": {
      "deletePrompt": "Delete prompt"
    },
    "prompts": {
      "ask": "Using the prompt above, you can ask any question about the company to get a formatted answer with a certainty score.",
      "openInDocs": "Open in API docs"
    },
    "values": {
      "false": "False",
      "true": "True"
    }
  },
  "es": {
    "badges": {
      "context": {
        "company": "Empresa",
        "list": "Lista",
        "team": "Equipo"
      },
      "feature": {
        "ask": "Preguntar",
        "enrich": "Enriquecer",
        "search": "Buscar",
        "similar": "Similar"
      },
      "processing": "Procesando",
      "score": "Certidumbre"
    },
    "buttons": {
      "deletePrompt": "Eliminar prompt"
    },
    "prompts": {
      "ask": "Usando el prompt anterior, puedes hacer cualquier pregunta sobre la empresa para obtener una respuesta formateada con un puntaje de certeza.",
      "openInDocs": "Abrir en docs de API"
    },
    "values": {
      "false": "Falso",
      "true": "Verdadero"
    }
  },
  "fr": {
    "badges": {
      "context": {
        "company": "Entreprise",
        "list": "Liste",
        "team": "Équipe"
      },
      "feature": {
        "ask": "Question",
        "enrich": "Enrichir",
        "search": "Recherche",
        "similar": "Similaire"
      },
      "processing": "Traitement",
      "score": "Certitude"
    },
    "buttons": {
      "deletePrompt": "Supprimer le prompt"
    },
    "prompts": {
      "ask": "En utilisant le prompt ci-dessus, vous pouvez poser n'importe quelle question sur l'entreprise pour obtenir une réponse formatée avec un score de certitude.",
      "openInDocs": "Ouvrir dans la documentation API"
    },
    "values": {
      "false": "Faux",
      "true": "Vrai"
    }
  }
}
</i18n>
